import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CardWrapper } from 'components'
import { TopToolbar } from 'components/lists'
import { addModal } from 'features/modals/modalSlice'
import { _ } from 'utils'
import routes from 'routes'
import {
    ShowsQueryVariables, ShowOrder
} from 'generated/graphql'
import { useEventSlug } from 'hooks'
import FullPageLoading from 'components/FullPageLoading'
import styled from 'styled-components'
import { useProgramItems } from './hooks'
import { Pagination } from '../pagination'
import { handleSubmit, importProps } from './importConfigs'
import AddDropdownButton from './components/AddDropdownButton'
import ProgramRow from './components/ProgramRow'
import CreateUpdateShowContainer from './CreateUpdateShowContainer'
import ProgramContainerRow from './components/ProgramContainerRow'

const S = {
    Wrapper: styled.div`
        width: 100%;
`
}

const ShowList = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const history = useHistory()
    const [listFilter, setListFilter] = useState('')
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<ShowsQueryVariables['sort']>(ShowOrder.DatetimeAsc)
    const upcomingOnly = listFilter==='upcoming'

    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = useProgramItems({
        eventId: eventSlug,
        search,
        sort,
        upcomingOnly,
    })

    const filterOptions = [
        { label: _('show.list.all_shows'), value: '' },
        { label: _('show.list.upcoming_shows'), value: 'upcoming' },
    ]
    const sortOptions = useMemo(() => (
        [
            { value: ShowOrder.DatetimeAsc, label: _('show.list.sort.datetime_asc') },
            { value: ShowOrder.DatetimeDesc, label: _('show.list.sort.datetime_desc') },
        ]
    ), [])

    const showProgramContainerModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateUpdateShowContainer close={close} callGetShows={fetchData} />
            ),
            header: _('show_container.modal_header.create'),
        }))
    }

    const renderAddButton = useCallback(
        () => (
            <AddDropdownButton
                showProgramContainerModal={showProgramContainerModal}
                createProgram={() => history.push(routes.createShow.getPath({ eventSlug }))}
            />
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ), [eventSlug, history]
    )

    return (
        <S.Wrapper>
            <CardWrapper>
                <TopToolbar
                    renderAddButton={renderAddButton}
                    filters={[{
                        options: filterOptions,
                        defaultValue: filterOptions[0],
                        onChange: setListFilter,
                    }]}
                    onSearch={setSearch}
                    searchPlaceHolder={_('show.list.search_placeholder')}
                    sortOptions={sortOptions}
                    onSort={setSort}
                    bulkActions={{
                        importProps: {
                            ...importProps,
                            onSubmit: (data) => handleSubmit(eventSlug, data, dispatch, fetchData),
                        }
                    }}
                />
                <>
                    {requestStatus === 'loading' && <FullPageLoading isAbsolute fullWidth />}
                    {list.filter((row) => !!row).map((row) => (row?.isContainer ? (
                        <ProgramContainerRow row={row} key={row.id} fetchData={fetchData} />
                    ): (
                        <ProgramRow row={row} key={row.id} fetchData={fetchData} />
                    )))}

                    <Pagination
                        page={pagination?.page}
                        onPageChange={handlePageChange}
                        totalItems={totalItems}
                        disabled={requestStatus === 'loading'}
                    />
                </>
            </CardWrapper>
        </S.Wrapper>
    )
}

export default ShowList
